










































































































































































































































































































import dayjs, { Dayjs } from 'dayjs';
import en from 'dayjs/locale/en';
import ja from 'dayjs/locale/ja';
import * as R from 'ramda';
import VueI18n from 'vue-i18n';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { EventTriggerType, _Boolean } from '@/API';
import { TaskHistory } from '@/appsync/graphql/taskHistory';
import {
  getPreSignedUrlForDownloadingIFXAPFile,
  downloadFromPreSignedUrl,
} from '@/appsync/lambda/downloadFileViaConversionApiService';
import { executeJobImmediately } from '@/appsync/lambda/jobExecuteService';
import {
  JobExecutionStatus,
  TaskExecutionStatus,
} from '@/components/organisms/EventCalendarPanel.vue';
import { toLocaleDatetimeString } from '@/containers/globals/Util';
import { i18n } from '@/plugins/i18n/i18n';
import router from '@/router';
import { CalendarEvent } from '@/types/type';
import { isNotEmptyArray } from '@/utils/CommonUtils';
import {
  EventType,
  throwRetryFailedError,
  taskIconColor,
  taskIcon,
} from '@/utils/EventCalendarUtils';
import { newPortalDate } from '@/utils/PortalDate';
import { Language, ICON } from '@/utils/consts';

enum RetryOption {
  RetryAll,
  RetryFromFailed,
}

@Component
export default class EventHistoryDetail extends Vue {
  @Prop()
  private event!: CalendarEvent;

  @Prop()
  private loginName!: string;

  private downloading: boolean = false;
  private showingRetryDialog: boolean = false;
  private selectedRetryOption: RetryOption = RetryOption.RetryFromFailed;
  private RetryOption = RetryOption;
  private startFromTheFirstTask: number = 0;

  private get showEventIconColor(): string | undefined {
    switch (this.event.eventType) {
      case EventType.PublicHoliday:
        return ICON.PUBLIC_HOLIDAY.COLOR;
      case EventType.CustomHoliday:
        return ICON.CUSTOM_HOLIDAY.COLOR;
      case EventType.NormalEvent:
        return this.event.iconColor;
      default: {
        const inspector: never = this.event.eventType;
        return this.event.iconColor;
      }
    }
  }

  private getTaskTitle(taskHistory: TaskHistory): string {
    // taskHistoryテーブルにtaskTitleカラム追加前のデータ対応
    // 古いデータではtaskHistory.taskTitleがnullの場合があるため、task.titleを代わりに使用する
    return taskHistory && taskHistory.taskTitle
      ? taskHistory.taskTitle
      : taskHistory.task.title;
  }

  private get retryTooltip() {
    if (this.isEventWithoutScheduledEventMetadata) {
      return this.$t(
        'jobScheduler.events.retryDisabledDueToNoEventMetadataToolTip'
      );
    }
    return this.$t('jobScheduler.events.retryTooltip');
  }

  private get viewTooltip() {
    if (this.isEventWithoutScheduledEventMetadata) {
      return this.$t(
        'jobScheduler.common.viewDisabledDueToNoEventMetadataToolTip'
      );
    }
    return this.$t('jobScheduler.common.viewTooltip');
  }

  private get copyTooltip() {
    if (this.isEventWithoutScheduledEventMetadata) {
      return this.$t(
        'jobScheduler.common.copyDisabledDueToNoEventMetadataToolTip'
      );
    }
    return this.$t('jobScheduler.common.copyTooltip');
  }

  private get showEventIcon(): string | undefined {
    switch (this.event.eventType) {
      case EventType.PublicHoliday:
        return ICON.PUBLIC_HOLIDAY.NAME;
      case EventType.CustomHoliday:
        return ICON.CUSTOM_HOLIDAY.NAME;
      case EventType.NormalEvent:
        return this.event.icon;
      default: {
        const inspector: never = this.event.eventType;
        return this.event.icon;
      }
    }
  }
  private localeEventStart(): Dayjs {
    return i18n.locale === Language.ja
      ? dayjs(this.event.start).locale(ja)
      : dayjs(this.event.start).locale(en);
  }

  private async downloadOutput() {
    if (this.event.eventHistoryId) {
      this.downloading = true;
      const signedUploadUrl = await getPreSignedUrlForDownloadingIFXAPFile(
        this.event.eventHistoryId
      );
      await downloadFromPreSignedUrl(signedUploadUrl);
      this.downloading = false;
    }
  }

  private get executionTime(): VueI18n.TranslateResult {
    return this.isPast
      ? this.$t('jobScheduler.eventCalendar.executedAt')
      : this.$t('jobScheduler.eventCalendar.executeAt');
  }

  private get executeCompletedTime() {
    return this.$t('jobScheduler.eventCalendar.executeCompletedAt');
  }

  private get formattedEventStart(): string {
    return toLocaleDatetimeString(this.event.start);
  }

  private get formattedEventEnd(): string {
    return this.event.updatedAt && this.isNotRunning
      ? toLocaleDatetimeString(this.event.updatedAt)
      : '-';
  }

  private get isPast(): boolean {
    const now = dayjs();
    const eventStartDateTime = dayjs(this.event.start);
    return eventStartDateTime.isBefore(now);
  }

  private get isTriggered(): boolean {
    return this.event.status !== JobExecutionStatus.NotTriggered;
  }

  private get isNotRunning(): boolean {
    return this.event.status !== JobExecutionStatus.Running;
  }

  private hasIncludeInDownloadTrue(): boolean {
    return R.pipe(
      R.pathOr([], ['taskHistories']),
      R.any(R.propEq('includeInDownload', _Boolean._true))
    )(this.event);
  }

  private get isDownloadEnabled(): boolean | undefined {
    return (
      this.event.hasOutputFile &&
      this.hasIncludeInDownloadTrue() &&
      R.includes(this.event.status, [
        JobExecutionStatus.Succeeded,
        JobExecutionStatus.Failed,
      ])
    );
  }

  private get eventViewPath(): object {
    return this.isCustomHoliday
      ? { name: 'viewHoliday', params: { id: this.event.eventId } }
      : { name: 'viewEvent', params: { id: this.event.eventId } };
  }

  private get eventEditPath(): object {
    return this.isCustomHoliday
      ? { name: 'editHoliday', params: { id: this.event.eventId } }
      : { name: 'editEvent', params: { id: this.event.eventId } };
  }

  private get eventCopyPath(): object {
    return this.isCustomHoliday
      ? { name: 'copyHoliday', params: { id: this.event.eventId } }
      : { name: 'copyEvent', params: { id: this.event.eventId } };
  }

  private get jobPath(): object {
    return { name: 'job', params: { id: this.event.jobId } };
  }

  private get hasTaskHistory(): boolean {
    return isNotEmptyArray(this.event.taskHistories);
  }

  private isSkipped(status: TaskExecutionStatus): boolean {
    return status === TaskExecutionStatus.Skipped;
  }

  private get isPublicHoliday(): boolean {
    return this.event.eventType === EventType.PublicHoliday;
  }

  private get isCustomHoliday(): boolean {
    return this.event.eventType === EventType.CustomHoliday;
  }

  private get isNormalEvent(): boolean {
    return this.event.eventType === EventType.NormalEvent;
  }

  private get isFailedEvent(): boolean {
    return (
      this.event.eventType === EventType.NormalEvent &&
      this.event.status === JobExecutionStatus.Failed
    );
  }

  private get isEventWithoutScheduledEventMetadata(): boolean {
    return Boolean(this.event.isEventWithoutScheduledEventMetadata);
  }

  private get canShowEditIcon(): boolean {
    return (
      (this.isNormalEvent && !this.isPast && !this.isTriggered) ||
      (this.isCustomHoliday && !this.isPast)
    );
  }

  private get canShowViewIcon(): boolean {
    return (
      (this.isNormalEvent && this.isPast) ||
      (this.isNormalEvent && this.isTriggered) ||
      (this.isCustomHoliday && this.isPast)
    );
  }

  private getTaskIconColor(taskHistory: TaskHistory) {
    return taskIconColor(taskHistory);
  }

  private getTaskIcon(taskHistory: TaskHistory) {
    return taskIcon(taskHistory);
  }

  private clickAbort(): void {
    // TODO:call api and receive result
    alert('abort');
  }

  private clickClose(): void {
    this.$emit('close');
  }

  private clickRetry(event: CalendarEvent) {
    this.showingRetryDialog = true;
  }

  private async doRetry() {
    if (!this.event.jobId) {
      throwRetryFailedError(this.event.eventId, this.event.eventHistoryId);
      this.showingRetryDialog = false;
      return;
    }

    const jobId = this.event.jobId;
    switch (this.selectedRetryOption) {
      case RetryOption.RetryAll:
        this.doRetryAll(jobId);
        break;
      case RetryOption.RetryFromFailed:
        this.doRetryFromFailed(jobId);
        break;
      default: {
        const inspector: never = this.selectedRetryOption;
        break;
      }
    }
    this.showingRetryDialog = false;
    this.$router.go(0);
  }
  private calculateStartIndex() {
    const taskHistories = this.event.taskHistories;
    if (!taskHistories) {
      throwRetryFailedError(this.event.eventId, this.event.eventHistoryId);
      this.showingRetryDialog = false;
      return -1;
    }

    const sortedTaskHistories = R.sortBy(R.prop('order'))(taskHistories);
    for (let i = 0; i < sortedTaskHistories.length; i++) {
      if (
        sortedTaskHistories[i].taskExecutionStatus ===
        TaskExecutionStatus.Failed
      ) {
        return i;
      }
    }
    return -1;
  }

  private async doRetryAll(jobId: string) {
    await executeJobImmediately(
      jobId,
      this.loginName,
      EventTriggerType.Retry,
      this.startFromTheFirstTask,
      this.event.title
    );
  }

  private async doRetryFromFailed(jobId: string) {
    const startIndex = this.calculateStartIndex();
    if (startIndex === -1) {
      throwRetryFailedError(this.event.eventId, this.event.eventHistoryId);
      this.showingRetryDialog = false;
      return;
    }
    await executeJobImmediately(
      jobId,
      this.loginName,
      EventTriggerType.Retry,
      startIndex,
      this.event.title
    );
  }
}
